import { AIModel } from '@/types';

export type ModelTask = 'general' | 'coding' | 'reasoning' | 'tool-use' | 'vision' | 'multilingual' | 'privacy' | 'fast';
export type ModelBrowserTab = 'recommended' | 'all' | 'local' | 'frontier';

const toLowerArray = (values?: string[] | null) =>
  Array.isArray(values) ? values.map((value) => value.toLowerCase()) : [];

const includesAny = (values: string[], needles: string[]) => needles.some((needle) => values.includes(needle));

const normalizeSearchValue = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9.+#-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

function getModelBrandLabel(model: AIModel) {
  const haystack = [model.name, model.family, model.apiName, model.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  if (/deepseek/.test(haystack)) return 'DeepSeek';
  if (/qwen/.test(haystack)) return 'Qwen';
  if (/gemma/.test(haystack)) return 'Gemma';
  if (/phi/.test(haystack)) return 'Phi';
  if (/smollm/.test(haystack)) return 'SmolLM';
  if (/stablelm|stability/.test(haystack)) return 'Stability';
  if (/llama|meta|hermes/.test(haystack)) return 'Meta';
  if (/mistral/.test(haystack)) return 'Mistral';
  if (/maplept/.test(haystack)) return 'MaplePT';
  return undefined;
}

export function isOnDeviceModel(model: AIModel) {
  if (model.runtime === 'webllm' || model.runtime === 'onnx') {
    return true;
  }

  if (model.provider === 'local' || model.provider === 'transformers') {
    return true;
  }

  return model.provider === 'ollama' && model.source === 'local';
}

export function isFrontierModel(model: AIModel) {
  return !isOnDeviceModel(model) && model.provider !== 'ollama';
}

export function getModelRuntimeLabel(model: AIModel) {
  if (isOnDeviceModel(model)) return 'OnDevice';
  if (model.provider === 'ollama' && model.source === 'remote') return 'Ollama remote';
  if (model.provider === 'maplept') return 'Frontier';
  if (['openai', 'claude', 'gemini', 'deepseek', 'cohere'].includes(model.provider)) return 'Frontier';
  return model.runtime === 'cloud' ? 'Cloud' : 'Cloud';
}

export function getModelProviderLabel(model: AIModel) {
  const brandLabel = getModelBrandLabel(model);

  switch (model.provider) {
    case 'openai':
      return 'OpenAI';
    case 'claude':
      return 'Claude';
    case 'gemini':
      return 'Gemini';
    case 'deepseek':
      return 'DeepSeek';
    case 'cohere':
      return 'Cohere';
    case 'maplept':
      return 'MaplePT';
    case 'ollama':
      return model.source === 'local' ? (brandLabel || 'Local Ollama') : 'Ollama';
    case 'local':
      return brandLabel || 'MapleOS';
    case 'transformers':
      return brandLabel || 'ONNX';
    default:
      return brandLabel || model.provider;
  }
}

export function getModelSizeLabel(model: AIModel) {
  if (model.size_label) return model.size_label;
  if (typeof model.size_gb !== 'number') return undefined;
  if (model.size_gb < 1) return 'Tiny';
  if (model.size_gb < 2.5) return 'Small';
  if (model.size_gb < 4.5) return 'Medium';
  return 'Large';
}

export function getModelQualityLabel(model: AIModel) {
  if (model.quality_label) return model.quality_label;
  const quantization = model.quantization?.toLowerCase();
  if (!quantization) return undefined;
  if (quantization.includes('q0f16') || quantization.includes('q0f32')) return 'High quality';
  if (quantization.includes('q4f16_1') || quantization.includes('q4f32_1')) return 'Balanced';
  if (quantization.includes('q3')) return 'Fast';
  return undefined;
}

export function formatContextLength(value?: number | null) {
  if (!value) return undefined;
  if (value >= 1000000) {
    return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M ctx`;
  }
  if (value >= 1000) {
    return `${Math.round(value / 1000)}K ctx`;
  }
  return `${value} ctx`;
}

export function formatSizeGb(value?: number | null) {
  if (typeof value !== 'number') return undefined;
  return `${value.toFixed(value < 1 ? 2 : 1)} GB`;
}

export function getModelDescriptionLine(model: AIModel) {
  return model.description || model.family || getModelProviderLabel(model);
}

function getModelSearchAliases(model: AIModel) {
  const haystack = [model.name, model.family, model.apiName, model.description, model.author]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const aliases = new Set<string>([
    model.provider,
    getModelProviderLabel(model),
    getModelRuntimeLabel(model),
  ]);

  if (model.provider === 'gemini' || /gemini|gemma/.test(haystack)) {
    aliases.add('google');
  }

  if (model.provider === 'openai' || /gpt|openai/.test(haystack)) {
    aliases.add('chatgpt');
  }

  if (model.provider === 'claude' || /claude|anthropic/.test(haystack)) {
    aliases.add('anthropic');
  }

  if (/phi/.test(haystack)) {
    aliases.add('microsoft');
  }

  if (/llama/.test(haystack)) {
    aliases.add('meta');
  }

  if (/qwen/.test(haystack)) {
    aliases.add('alibaba');
  }

  if (/mistral|mixtral/.test(haystack)) {
    aliases.add('mistral ai');
  }

  if (/command/.test(haystack) || model.provider === 'cohere') {
    aliases.add('cohere');
  }

  if (/deepseek/.test(haystack)) {
    aliases.add('deepseek ai');
  }

  return Array.from(aliases).filter(Boolean);
}

function getModelPrimarySearchText(model: AIModel) {
  return [
    model.name,
    model.family,
    model.apiName,
    model.author,
    model.provider,
    getModelProviderLabel(model),
    ...getModelSearchAliases(model),
  ]
    .filter(Boolean)
    .join(' ');
}

export function getModelSearchText(model: AIModel) {
  return [
    getModelPrimarySearchText(model),
    model.runtime,
    getModelRuntimeLabel(model),
    model.description,
    ...(model.capabilities || []),
    ...(model.use_cases || []),
    ...(model.recommended_for || []),
    ...(model.tags || []),
    ...(model.languages || []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
}

export function matchesModelSearch(model: AIModel, query: string) {
  const normalizedQuery = normalizeSearchValue(query);
  if (!normalizedQuery) return true;

  const primary = normalizeSearchValue(getModelPrimarySearchText(model));
  const secondary = normalizeSearchValue(getModelSearchText(model));

  if (primary.includes(normalizedQuery) || secondary.includes(normalizedQuery)) {
    return true;
  }

  const tokens = normalizedQuery.split(' ').filter(Boolean);
  if (tokens.length === 0) return true;

  return tokens.every((token) => primary.includes(token) || secondary.includes(token));
}

export function getModelBadges(model: AIModel) {
  const badges: string[] = [];
  const recommended = toLowerArray(model.recommended_for);
  const useCases = toLowerArray(model.use_cases);
  const capabilities = toLowerArray(model.capabilities);
  const languages = toLowerArray(model.languages);

  if (includesAny(recommended, ['coding']) || includesAny(useCases, ['coding', 'code']) || includesAny(capabilities, ['code'])) {
    badges.push('Coding');
  }
  if (model.supports_reasoning || includesAny(recommended, ['reasoning']) || includesAny(useCases, ['reasoning', 'math', 'step-by-step'])) {
    badges.push('Reasoning');
  }
  if (model.supports_tools || includesAny(recommended, ['tool-use', 'tools']) || includesAny(useCases, ['tool use', 'tools', 'function calling', 'json output', 'agentic'])) {
    badges.push('Tool use');
  }
  if (model.supports_vision || includesAny(capabilities, ['image', 'vision'])) {
    badges.push('Vision');
  }
  if (model.supports_multilingual || languages.length > 1 || includesAny(useCases, ['multilingual', 'bilingual chat'])) {
    badges.push('Multilingual');
  }
  if (model.is_official) {
    badges.push('Official');
  }
  if (model.runtime === 'webllm' || model.provider === 'local') {
    badges.push('On-device');
  }
  if (model.runtime === 'onnx' || model.provider === 'transformers') {
    badges.push('ONNX');
  }
  if (isFrontierModel(model)) {
    badges.push('Frontier');
  }

  return Array.from(new Set(badges)).slice(0, 4);
}

export function getModelTaskScore(model: AIModel, task: ModelTask) {
  const capabilities = toLowerArray(model.capabilities);
  const useCases = toLowerArray(model.use_cases);
  const recommended = toLowerArray(model.recommended_for);
  const languages = toLowerArray(model.languages);

  let score = 0;

  if (model.is_active === false) score -= 100;

  switch (task) {
    case 'coding':
      if (includesAny(recommended, ['coding'])) score += 5;
      if (includesAny(useCases, ['coding', 'json output', 'function calling'])) score += 4;
      if (includesAny(capabilities, ['code'])) score += 3;
      break;
    case 'reasoning':
      if (model.supports_reasoning) score += 6;
      if (includesAny(recommended, ['reasoning'])) score += 5;
      if (includesAny(useCases, ['reasoning', 'math', 'step-by-step'])) score += 4;
      break;
    case 'tool-use':
      if (model.supports_tools) score += 6;
      if (model.supports_json) score += 3;
      if (includesAny(useCases, ['function calling', 'json output', 'agentic', 'tool use'])) score += 4;
      break;
    case 'vision':
      if (model.supports_vision || includesAny(capabilities, ['image', 'vision'])) score += 10;
      else score -= 10;
      break;
    case 'multilingual':
      if (model.supports_multilingual) score += 6;
      if (languages.length > 1) score += 4;
      if (includesAny(useCases, ['multilingual', 'bilingual chat'])) score += 3;
      break;
    case 'privacy':
      if (isOnDeviceModel(model)) score += 10;
      if (model.runtime === 'onnx') score += 2;
      break;
    case 'fast':
      if (getModelSizeLabel(model) === 'Tiny') score += 7;
      if (getModelSizeLabel(model) === 'Small') score += 5;
      if (getModelQualityLabel(model) === 'Fast') score += 3;
      if (isOnDeviceModel(model)) score += 2;
      break;
    case 'general':
    default:
      if (isFrontierModel(model)) score += 5;
      if (includesAny(useCases, ['chat', 'general assistant'])) score += 3;
      if (model.context_length && model.context_length >= 32000) score += 1;
      break;
  }

  if (model.is_official) score += 1;
  if (model.context_length && model.context_length >= 32000) score += 1;
  if (typeof model.downloads === 'number') score += Math.min(model.downloads / 100000, 2);

  return score;
}

export function getTopModelsForTask(models: AIModel[], task: ModelTask, limit = 4) {
  return [...models]
    .sort((a, b) => getModelTaskScore(b, task) - getModelTaskScore(a, task))
    .slice(0, limit);
}

function getNameVersionScore(model: AIModel) {
  const matches: string[] = (model.name || '').match(/\d+(?:\.\d+)?/g) || [];
  return matches.reduce<number>((score, value, index) => {
    const numeric = Number(value);
    if (!Number.isFinite(numeric)) return score;
    return score + numeric / Math.pow(100, index);
  }, 0);
}

function pickNewestModel(models: AIModel[], predicate: (model: AIModel) => boolean) {
  return [...models]
    .filter((model) => model.is_active !== false)
    .filter(predicate)
    .sort((a, b) => {
      const versionDiff = getNameVersionScore(b) - getNameVersionScore(a);
      if (versionDiff !== 0) return versionDiff;
      return getModelTaskScore(b, 'general') - getModelTaskScore(a, 'general');
    })[0];
}

export function getBestOverallModels(models: AIModel[], limit = 3) {
  const frontierModels = models.filter((model) => model.is_active !== false && isFrontierModel(model));

  const curated = [
    pickNewestModel(frontierModels, (model) => model.provider === 'claude'),
    pickNewestModel(frontierModels, (model) => model.provider === 'openai' && /gpt/i.test(`${model.name} ${model.apiName}`)),
    pickNewestModel(frontierModels, (model) => model.provider === 'cohere' && /command\s*a/i.test(`${model.name} ${model.apiName}`)),
  ].filter((model): model is AIModel => Boolean(model));

  if (curated.length >= limit) {
    return curated.slice(0, limit);
  }

  const existingIds = new Set(curated.map((model) => model.id));
  const fallback = getTopModelsForTask(frontierModels, 'general', models.length)
    .filter((model) => !existingIds.has(model.id))
    .slice(0, Math.max(0, limit - curated.length));

  return [...curated, ...fallback];
}

export function getSuggestedTaskFromText(input: string, hasImageAttachment = false): ModelTask {
  const value = input.toLowerCase();
  if (hasImageAttachment) return 'vision';
  if (/(json|tool|function|agent|workflow|automation)/i.test(value)) return 'tool-use';
  if (/(code|typescript|javascript|python|bug|debug|refactor|sql|api)/i.test(value)) return 'coding';
  if (/(reason|math|proof|step by step|analyze|compare|logic)/i.test(value)) return 'reasoning';
  if (/(translate|multilingual|french|german|spanish|japanese|chinese)/i.test(value)) return 'multilingual';
  if (/(private|local|offline|on-device)/i.test(value)) return 'privacy';
  if (/(fast|quick|lightweight|small)/i.test(value)) return 'fast';
  return 'general';
}

export function getModelCompatibilityWarning(model: AIModel, task: ModelTask) {
  if (task === 'vision' && !(model.supports_vision || toLowerArray(model.capabilities).includes('image'))) {
    return 'This model may not handle image-heavy tasks well.';
  }

  if (task === 'tool-use' && !(model.supports_tools || model.supports_json)) {
    return 'This model is not ideal for tools, agents, or structured JSON work.';
  }

  if (task === 'privacy' && !isOnDeviceModel(model)) {
    return 'This task may be better with an on-device model for privacy.';
  }

  return undefined;
}

export function getTabModels(models: AIModel[], tab: ModelBrowserTab) {
  if (tab === 'local') {
    return models.filter(isOnDeviceModel);
  }

  if (tab === 'frontier') {
    return models.filter(isFrontierModel);
  }

  return models;
}